import { useCallback, useMemo } from 'react'
import { renderAgentMarkdown } from '@/components/agent-markdown/renderAgentMarkdown'
import { Button } from '@/components/ds2'
import { useGeneralAgentChatStore } from '../hooks/generalAgentChatStore'
import { UserBubble } from './UserBubble'
import type { GeneralChatMessage } from '../types'
import { threadLoadsOf, threadOf } from './results/cardResults'
import { useThreadCards } from './results/cardResultsStore'
import { AgentAvatar, AnswerBlock } from './results/AnswerBlock'
import { stateOfCard, turnsOf, updatedBelowOf } from './results/turns'
import { threadInvoicesOf, ThreadInvoicesProvider } from './results/threadInvoices'
import { useThreadStoredAssigns } from './assign/storedAssignStore'
import { chatAssignsOf, newestAssigns, storedAssignsNewerThan } from './assign/storedAssigns'
import { StoredAssignsProvider } from './assign/StoredAssignsContext'

interface GeneralAgentMessageListProps {
    messages: GeneralChatMessage[]
    waiting: boolean
}

export const GeneralAgentMessageList = ({ messages, waiting }: GeneralAgentMessageListProps) => {
    const tabId = useGeneralAgentChatStore((s) => s.tabId)
    const send = useGeneralAgentChatStore((s) => s.send)
    const notes = useGeneralAgentChatStore((s) => s.notes)
    // A card's rows are drawn where its result came, between the user's message and the reply that sums it up (FR-G3).
    const cards = useThreadCards(tabId, messages)
    // The stored direct writes and the chat's own turn writes, newest of each load; a newer loads card already showing it wins.
    const storedRead = useThreadStoredAssigns(tabId, messages)
    const storedAssigns = useMemo(() => storedAssignsNewerThan(newestAssigns(storedRead, chatAssignsOf(cards)), cards), [storedRead, cards])
    const turns = useMemo(() => turnsOf(threadOf(messages, cards)), [messages, cards])
    const loads = useMemo(() => threadLoadsOf(cards), [cards])
    // A load a newer block shows in another state is dimmed in the older blocks, which say "Updated below ↓" (design §12).
    const updatedBelow = useMemo(() => {
        const blocks = turns.flatMap((item) => (item.kind === 'turn' ? [item] : []))
        const keys = updatedBelowOf(blocks.map((block) => stateOfCard(block.cards)))
        return new Map(blocks.map((block, i) => [block.id, keys[i]]))
    }, [turns])
    // A load the chat invoiced reads as invoiced on every card, the ones drawn before the invoice too.
    const invoiced = useMemo(() => threadInvoicesOf(cards), [cards])
    // Stable, so a card already drawn is not drawn again on every render of the list.
    const onSend = useCallback((message: string) => void send(message), [send])

    // An empty chat is drawn by the chat shell (its empty state), so the list has nothing to show.
    if (!messages.length && !waiting && !notes.length) return null
    return (
        <StoredAssignsProvider assigns={storedAssigns}>
            <ThreadInvoicesProvider invoiced={invoiced}>
                <ol className="flex flex-col gap-3" aria-live="polite">
                    {/* No AI Hub load table here, so replies keep `#N` as plain text (no `loadRefJump`). */}
                    {turns.map((item) => {
                        if (item.kind === 'turn') {
                            return (
                                <li key={item.id} className="flex">
                                    <AnswerBlock
                                        cards={item.cards}
                                        replies={item.replies}
                                        asOf={item.asOf}
                                        loads={loads}
                                        updatedBelow={updatedBelow.get(item.id)}
                                        question={item.question}
                                        onSend={onSend}
                                    />
                                </li>
                            )
                        }
                        const m = item.message
                        if (m.message_type === 'user') {
                            return <UserBubble key={m.id} content={m.content ?? ''} />
                        }
                        // A text-only agent reply: the ✦ mark and the words, with no card surface (v17).
                        return (
                            <li key={m.id} className="flex max-w-[95%] gap-3 self-start text-foreground">
                                <AgentAvatar />
                                <div className="min-w-0 flex-1 pt-0.5 text-sm">{renderAgentMarkdown(m.content ?? '', { links: true })}</div>
                            </li>
                        )
                    })}
                    {/* The panel's actions, as short lines in this chat only (the store's `notes`). */}
                    {notes.map((note, index) =>
                        typeof note === 'string' ? (
                            <li key={`note-${index}`} className="self-center text-xs text-foreground-muted">
                                {note}
                            </li>
                        ) : (
                            // An agent message a panel action wrote (a prepared batch): the ✦ mark, its words and its next step.
                            <li key={`note-${index}`} className="flex max-w-[95%] gap-3 self-start text-foreground">
                                <AgentAvatar />
                                <div className="flex min-w-0 flex-1 flex-col items-start gap-2 pt-0.5 text-sm">
                                    <p>{note.text}</p>
                                    {note.action && (
                                        <Button size="sm" variant="outline" disabled={waiting} onClick={() => onSend(note.action!.message)}>
                                            {note.action.label}
                                        </Button>
                                    )}
                                </div>
                            </li>
                        )
                    )}
                </ol>
            </ThreadInvoicesProvider>
        </StoredAssignsProvider>
    )
}
